import "VSS/LoaderPlugins/Css!Controls/ImportCSVPanel";

import * as React from "react";
import * as ReactDOM from "react-dom";

import * as CustomerIntelligenceConstants from "WorkItemTracking/Scripts/CustomerIntelligence";
import { Panel, PanelType, IPanelProps } from "OfficeFabric/Panel";
import { PrimaryButton, DefaultButton } from "OfficeFabric/Button";
import { MessageBar, MessageBarType } from "OfficeFabric/MessageBar";
import { Spinner, SpinnerSize } from "OfficeFabric/Spinner";
import { TooltipHost, TooltipOverflowMode } from "OfficeFabric/Tooltip";

import * as WITResources from "WorkItemTracking/Scripts/Resources/TFS.Resources.WorkItemTracking";
import { ProjectCollection } from "Presentation/Scripts/TFS/TFS.OM.Common";
import * as Telemetry from "VSS/Telemetry/Services";
import { TfsContext } from "Presentation/Scripts/TFS/TFS.Host.TfsContext";
import { WorkItemStore } from "WorkItemTracking/Scripts/TFS.WorkItemTracking";
import { QueryResultCSVImporter, IWorkItemsImportData } from "WorkItemTracking/Scripts/Controls/QueryResultCSVImporter";

import { registerLWPComponent } from "VSS/LWP";

export interface IImportCSVPanelProps {
    /*
     * Callback called when panel is dismissed by either clicking Cancel or hitting Esc
     */
    onDismiss?: (isLoading: boolean) => void;

    /*
     * Callback called when user clicks Import button in the panel
     */
    onImportClick: (csvImportData: IWorkItemsImportData) => void;
}

export interface IImportCSVPanelState {
    csvString: string;
    csvImportData: IWorkItemsImportData;
    isLoading: boolean;
    error: string;
    percentComplete: number;
}

export class ImportCSVPanel extends React.Component<IImportCSVPanelProps, IImportCSVPanelState> {
    private _queryResultCSVImporter: QueryResultCSVImporter;
    private _fileInputRef = React.createRef<HTMLInputElement>();
    private _fileNameRef = React.createRef<HTMLSpanElement>();
    private _container;

    private c_csvExtension = "csv";

    constructor(props: IImportCSVPanelProps) {
        super(props);

        const tfsContext = TfsContext.getDefault();
        const store = ProjectCollection.getConnection(tfsContext).getService<WorkItemStore>(WorkItemStore);
        this._queryResultCSVImporter = new QueryResultCSVImporter(store, tfsContext.navigation.project, this.updatePercentComplete.bind(this));

        const buttonLabel = React.createElement(DefaultButton, {
            text: WITResources.ChooseFile,
            onClick: () => { this._fileInputRef.current?.click(); },
            ariaLabel: WITResources.ChooseFile,
            className: "importcsv-choose-file-button"
        });
        const button = React.createElement("input", {
            type: "file",
            accept: ".csv",
            ref: this._fileInputRef,
            style: { display: "none" },
            onChange: e => {
                this._filterFilesAndUpdateState(e.target.files);
            }
        });
        const fileNameLabel = React.createElement("span", {
            className: "margin-left-8 text-ellipsis",
            ref: this._fileNameRef
        }, "");
        const toolTip = React.createElement(TooltipHost, {
            tooltipProps: {
                onRenderContent: () => {
                    return <div>{this._fileNameRef.current?.innerText}</div>;
                }
            },
            hostClassName: "text-ellipsis",
            overflowMode: TooltipOverflowMode.Self
        }, fileNameLabel);

        this._container = React.createElement("div", { className: "workitemcontrol work-item-control initialized" }, buttonLabel, button, toolTip); //, style: { color: "red", backgroundColor: "black", height: "100px", width: "100px" } }, button);

        this.state = {
            csvString: null,
            csvImportData: null,
            isLoading: false,
            error: null,
            percentComplete: 0
        };
    }

    public static componentType = "importCSVPanel";

    public render(): JSX.Element {
        return (
            <Panel
                isOpen={true}
                onRenderHeader={this._onRenderHeader}
                closeButtonAriaLabel={WITResources.ClosePanel}
                headerText={WITResources.ImportWorkItems}
                type={PanelType.custom}
                customWidth="450px"
                isLightDismiss={false}
                isFooterAtBottom={true}
                className="column-options-panel"
                onRenderFooterContent={this._onRenderFooterContent}
                onDismiss={this._onDismiss}
                layerProps={{ className: "column-options-panel-layer" }}
            >
                {this._renderContents()}
            </Panel>
        );
    }

    private _onRenderHeader = (props?: IPanelProps, defaultRender?: (props?: IPanelProps) => JSX.Element) => {
        return <div className="column-options-panel-header">{defaultRender(props)}</div>;
    };

    public updatePercentComplete(newPercentComplete: number): void {
        this.setState({ percentComplete: newPercentComplete });
    }

    private _renderContents(): React.ReactNode {
        const hasErrors = this.state.error !== null;
        const isLoading = this.state.isLoading;

        const percentCompleteMessage = `${String(Math.floor(this.state.percentComplete * 100))}%`;

        return (
            <div className="importcsv-panel-contents">
                {!isLoading && (
                    <div className="file-container">
                        <div className="importcsv-panel-title">{WITResources.ImportCSVPanelText}</div>
                        {this._container}
                    </div>
                )}
                {isLoading && (
                    <div>
                        <div>{WITResources.ImportCSVPanelLoadingText}</div>
                        <Spinner className="importcsv-loading-spinner" size={SpinnerSize.large} label={percentCompleteMessage} />
                    </div>
                )}
                {hasErrors && (
                    <div>
                        <div className="importcsv-error-title">{WITResources.ImportCSVErrorHeader}</div>
                        <MessageBar messageBarType={MessageBarType.error}>{this.state.error}</MessageBar>
                    </div>
                )}
            </div>
        );
    }

    private _filterFilesAndUpdateState(files: FileList): void {
        this.setState({ error: null });

        const reader = new FileReader();
        let csvFile: File = null;

        // Filter for 1st instance of CSV file
        for (let i = 0; i < files.length; i++) {
            const extension = files[i].name.split(".").pop();
            if (extension === this.c_csvExtension) {
                csvFile = files[i];
                this._fileNameRef.current.innerText = csvFile.name;
                break;
            }
        }

        if (csvFile === null) {
            this.setState({ error: WITResources.InvalidCSVFileFormat });
            this._fileNameRef.current.innerText = "";
            return;
        }

        reader.onload = () => {
            const result: string = reader.result.toString();
            this.setState({ csvString: result });
        };

        reader.readAsText(csvFile);
    }

    private _onRenderFooterContent = (): JSX.Element => {
        const isLoading = this.state.isLoading;
        return (
            <div style={{ textAlign: "right" }}>
                {!isLoading && (
                    <PrimaryButton
                        ariaLabel={WITResources.Import}
                        disabled={!this._isCSVDataValid()}
                        onClick={this._onImportClick}
                        style={{ marginRight: "8px" }}
                    >
                        {WITResources.Import}
                    </PrimaryButton>
                )}
                <DefaultButton ariaLabel={WITResources.Cancel} onClick={this._onDismiss}>
                    {WITResources.Cancel}
                </DefaultButton>
            </div>
        );
    };

    private _isCSVDataValid(): boolean {
        return this.state.csvString !== null;
    }

    protected _onImportClick = () => {
        this.setState({ isLoading: true, error: null });

        this._queryResultCSVImporter
            .importCSVFile(this.state.csvString)
            .then(data => {
                this.setState({
                    isLoading: false,
                    csvImportData: data,
                    percentComplete: 100
                });

                this.props.onImportClick(this.state.csvImportData);
            })
            .catch(reason => {
                const error = reason.message || reason;
                Telemetry.publishEvent(new Telemetry.TelemetryEventData(
                    CustomerIntelligenceConstants.WITCustomerIntelligenceArea.WORK_ITEM_TRACKING,
                    CustomerIntelligenceConstants.WITCustomerIntelligenceFeature.IMPORTCSV_IMPORT_ERROR,
                    { error, errorType: reason.errorType || "ValidationError" }
                ));
                this.setState({
                    isLoading: false,
                    error,
                    csvImportData: null,
                    percentComplete: 0,
                    csvString: null
                });
            });
    }

    private _onDismiss = () => {
        this._queryResultCSVImporter.dispose();
        this.props.onDismiss(this.state.isLoading);
    }
}

export function showImportCSVPanel(props: IImportCSVPanelProps): void {
    const panelNode = document.createElement("div");
    const panelProps: IImportCSVPanelProps = {
        ...props,
        onDismiss: (isLoading?: boolean) => {
            ReactDOM.unmountComponentAtNode(panelNode);
            if (props.onDismiss) {
                props.onDismiss(isLoading);
            }
        },
        onImportClick: (csvImportData: IWorkItemsImportData) => {
            ReactDOM.unmountComponentAtNode(panelNode);
            props.onImportClick(csvImportData);
        }
    };

    ReactDOM.render(<ImportCSVPanel {...panelProps} />, panelNode);
}

registerLWPComponent(ImportCSVPanel.componentType, ImportCSVPanel);
